import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Flex,
  Heading,
  Skeleton,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from '@chakra-ui/react';
import React from 'react';

import type { BankAccountWithInstitutionAndOwnerGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { BankAccountForm } from './BankAccountForm';
import { getFormattedBankAccountDefaultValues } from './bankAccountFormConfigs';
import { DeactivateBankAccountConfirmation } from './DeactivateBankAccountConfirmation';
import { useGetBankAccountsWithInstitutionAndOwnerByImplementationIdQuery } from '~/packages/bankAccounts/requests/bankAccounts.gql.generated';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from '~/packages/common-frontend/utils';
import { GetOrganizationImplementationsDetailsQueryGQLType } from '~/packages/implementations/requests/implementations.gql.generated';

// Type alias for implementation from the client query
type OrganizationImplementationFragmentGQLType = GetOrganizationImplementationsDetailsQueryGQLType['organization']['implementations'][0];

// Helpers
const getConnectionAccounts = (
  bankAccount: BankAccountWithInstitutionAndOwnerGQLType,
): string[] => {
  const connectionProviders: string[] = [];
  if (bankAccount.tellerAccountId) {
    connectionProviders.push('Teller');
  }

  if (bankAccount.plaidAccountId) {
    connectionProviders.push('Plaid');
  }

  if (!bankAccount.plaidAccountId && !bankAccount.tellerAccountId) {
    connectionProviders.push('None');
  }

  return connectionProviders;
};

// Exports
type Props = {
  implementation: OrganizationImplementationFragmentGQLType;
  organizations: { id: string; slug: string; name: string }[];
};
export const BankAccountsTable = ({ implementation, organizations }: Props) => {
  const toast = useToast();
  const {
    data,
    loading,
  } = useGetBankAccountsWithInstitutionAndOwnerByImplementationIdQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      implementationId: implementation.id,
      maskSensitiveInfo: false,
    },
  });

  if (loading || !data) {
    return <Skeleton />;
  }

  const bankAccounts = data.implementation.bankAccountsWithInstitutionAndOwner;

  return (
    <Box mb="35px">
      <Flex justifyContent="space-between" padding="25px 0px">
        <Heading display="inline-block" fontSize="3xl" as="h3">
          Bank Accounts
        </Heading>
        <BankAccountForm
          implementation={implementation}
          organizations={organizations}
          mode="add"
        />
      </Flex>
      <Table>
        <Thead>
          <Tr>
            <Th>Id</Th>
            <Th>Account Holder</Th>
            <Th>Account Name</Th>
            <Th>Institution</Th>
            <Th>Connection Provider</Th>
            <Th>Edit</Th>
            <Th>Deactivate</Th>
          </Tr>
        </Thead>
        <Tbody>
          {bankAccounts.map((ba) => {
            const formattedBankAccountDefaultValues = getFormattedBankAccountDefaultValues(
              {
                organizationId: ba.organizationId,
                bankInstitutionId: ba.bankInstitutionId,
                accountName: ba.name,
                routingNumber: ba.routingNumber,
                accountNumber: ba.accountNumber,
                tellerAccountId: ba.tellerAccountId,
                plaidAccountId: ba.plaidAccountId,
              },
            );

            const accountNumberLastFourDigits = ba.accountNumber.substring(
              ba.accountNumber.length - 4,
            );
            const routingNumberLastFourDigits = ba.routingNumber.substring(
              ba.routingNumber.length - 4,
            );

            return (
              <Tr key={ba.id}>
                <Td>{ba.id}</Td>
                <Td>{ba.ownerName}</Td>
                <Td>{`${ba.name} (${accountNumberLastFourDigits})`}</Td>
                <Td>{`${ba.institutionName} (${routingNumberLastFourDigits})`}</Td>
                <Td whiteSpace="pre-wrap">
                  {getConnectionAccounts(ba).map((connectionProvider) => {
                    if (ba.plaidAccountId || ba.tellerAccountId) {
                      return (
                        <Flex
                          key={`${ba.connectionProviderName} (${ba.connectionProviderAccountMask})`}
                          alignItems="center"
                          gap="0.25rem"
                        >
                          {`${connectionProvider} `}
                          <Tooltip
                            key={`key-${Math.random()}`}
                            placement="top"
                            label={`${ba.connectionProviderName} (${ba.connectionProviderAccountMask})`}
                          >
                            <InfoOutlineIcon />
                          </Tooltip>
                        </Flex>
                      );
                    }
                    return `${connectionProvider}\n`;
                  })}
                </Td>
                <Td>
                  <BankAccountForm
                    implementation={implementation}
                    organizations={organizations}
                    bankAccountId={ba.id}
                    formattedBankAccountDefaultValues={
                      formattedBankAccountDefaultValues
                    }
                    mode="edit"
                  />
                </Td>
                <Td>
                  <DeactivateBankAccountConfirmation
                    bankAccountId={ba.id}
                    institutionName={`${ba.institutionName} (${routingNumberLastFourDigits})`}
                    accountName={`${ba.name} (${accountNumberLastFourDigits})`}
                  />
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
      {bankAccounts.length === 0 && (
        <Alert>
          <AlertIcon />
          <AlertDescription>No bank accounts configured</AlertDescription>
        </Alert>
      )}
    </Box>
  );
};
